<script>
import axios from "axios";
import { nextTick } from "vue";
import YoutubeVideo from "@/components/VideoPlayer.vue";
import AudioPlayer from "@/components/AudioPlayer.vue";
import HawamPlayer from "@/components/HawamPlayer.vue";
import PdfLearningViewer from "@/components/pdf/PdfLearningViewer.vue";
import youtubeIcon from "@/assets/Icons/youtube.vue";
import pdf from "@/assets/Icons/pdf.vue";
import audio from "@/assets/Icons/audio.vue";

/**
 * "Course Content Mode" browser — the same branch/unit/lesson/content tree UI
 * as contents.vue, but scoped to a single course or academic year via
 * course_id/academic_year_id instead of the student's global education_level.
 * Reused by ProductDetails.vue so subscribed students see their lessons,
 * videos, audio and PDFs inline once they have access, without duplicating
 * the fetch/accordion logic that already lives in contents.vue.
 */
export default {
  name: "CourseContentBrowser",
  components: { AudioPlayer, YoutubeVideo, HawamPlayer, PdfLearningViewer, youtubeIcon, pdf, audio },
  props: {
    courseId: { type: [String, Number], default: null },
    academicYearId: { type: [String, Number], default: null },
  },
  data() {
    return {
      user: JSON.parse(localStorage.getItem("user") || "{}"),
      branches: [],
      units: [],
      content: null,
      videoId: "",
      openedBranchId: null,
      openedUnitId: null,
      openedLessonId: null,
      selectedContentId: null,
      activePlayer: "youtube",
      sidebarOpen: true,
      isMobile: window.innerWidth <= 768,
    };
  },
  computed: {
    scopeParams() {
      const p = {};
      if (this.courseId) p.course_id = this.courseId;
      if (this.academicYearId) p.academic_year_id = this.academicYearId;
      return p;
    },
  },
  methods: {
    fetchBranch() {
      const params = { type: 1, ...this.scopeParams };
      axios
        .get("/web/lessons", { params })
        .then((response) => { this.branches = response.data.data; })
        .catch(() => { this.branches = []; });
    },

    fetchUnits(branchId) {
      const params = { type: 2, parent_id: branchId, ...this.scopeParams };
      axios
        .get("/web/lessons", { params })
        .then((response) => {
          this.units = response.data.data.map((u) => ({ ...u, lessons: [] }));
        })
        .catch(() => { this.units = []; });
    },

    async fetchLessons(unitId) {
      try {
        const params = { type: 3, parent_id: unitId, ...this.scopeParams };
        const response = await axios.get("/web/lessons", { params });
        const unit = this.units.find((u) => u.id === unitId);
        if (unit) {
          unit.lessons = response.data.data.map((l) => ({ ...l, contents: [] }));
        }
        await nextTick();
      } catch (e) { console.error(e); }
    },

    async fetchContents(lessonId) {
      try {
        const params = { lesson_id: lessonId, ...this.scopeParams };
        const response = await axios.get("/web/contents", { params });
        const unit = this.units.find((u) => u.id === this.openedUnitId);
        if (unit) {
          const lesson = unit.lessons.find((l) => l.id === lessonId);
          if (lesson) lesson.contents = response.data.data;
        }
        await nextTick();
      } catch (e) { console.error(e); }
    },

    handleBranchToggle(branchId, event) {
      const details = event.target;
      if (details.open && this.openedBranchId !== branchId) {
        this.openedBranchId = branchId;
        this.openedUnitId = null;
        this.openedLessonId = null;
        this.units = [];
        this.fetchUnits(branchId);
        if (this.isMobile) this.sidebarOpen = true;
      } else if (!details.open) {
        this.openedBranchId = null;
        this.openedUnitId = null;
        this.openedLessonId = null;
        this.units = [];
      }
    },

    handleUnitToggle(unitId) {
      if (this.openedUnitId !== unitId) {
        this.openedUnitId = unitId;
        this.openedLessonId = null;
        this.fetchLessons(unitId);
      } else {
        this.openedUnitId = null;
        this.openedLessonId = null;
      }
    },

    handleLessonToggle(lessonId) {
      if (this.openedLessonId !== lessonId) {
        this.openedLessonId = lessonId;
        this.fetchContents(lessonId);
      } else {
        this.openedLessonId = null;
      }
    },

    showContent(content) {
      this.selectedContentId = content.id;
      this.content = null;
      setTimeout(() => {
        this.content = content;
        if (content.type === "video" && content.link) {
          const match = content.link.match(/(?:v=|\/embed\/|\.be\/|watch\?v=)([A-Za-z0-9_-]{11})/);
          this.videoId = match && match[1] ? match[1] : "";
        } else {
          this.videoId = "";
        }
        if (this.isMobile) {
          this.sidebarOpen = false;
          nextTick(() => {
            if (this.$refs.viewer) {
              this.$refs.viewer.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          });
        }
      }, 10);
    },

    getContentTypeIcon(type) {
      if (type === "video") return "▶";
      if (type === "audio") return "🎵";
      if (type === "pdf")   return "📄";
      return "📁";
    },
  },
  mounted() {
    this.fetchBranch();
    this._onResize = () => { this.isMobile = window.innerWidth <= 768; };
    window.addEventListener("resize", this._onResize);
  },
  beforeUnmount() { window.removeEventListener("resize", this._onResize); },
};
</script>

<template>
  <div class="ccb-root">
    <!-- ── Branch tabs ── -->
    <div class="ccb-branches-strip">
      <div
        v-for="branch in branches"
        :key="branch.id"
        class="ccb-branch-item"
        :class="{ active: openedBranchId === branch.id }"
        @click="handleBranchToggle(branch.id, { target: { open: openedBranchId !== branch.id } })"
      >
        <p>{{ branch?.title }}</p>
        <span class="ccb-branch-description">{{ branch?.description }}</span>
      </div>

      <div v-if="!branches.length" class="ccb-branches-empty">
        جاري تحميل الفصول...
      </div>
    </div>

    <!-- ── Main content layout ── -->
    <div class="ccb-layout">

      <!-- Sidebar toggle (tablet 768–960px only) -->
      <button class="ccb-sidebar-toggle-btn" @click="sidebarOpen = !sidebarOpen">
        {{ sidebarOpen ? "← إخفاء القائمة" : "☰ عرض القائمة" }}
      </button>

      <!-- ── Mobile visible units panel header ── -->
      <div v-if="isMobile" class="ccb-mobile-panel-header" @click="sidebarOpen = !sidebarOpen">
        <div class="ccb-panel-header-label">
          <i class="pi pi-list"></i>
          <span>الوحدات والدروس</span>
          <span v-if="units.length" class="ccb-panel-badge">{{ units.length }}</span>
        </div>
        <div class="ccb-panel-header-action">
          <span class="ccb-panel-status-text">{{ sidebarOpen ? "إخفاء" : "عرض" }}</span>
          <i :class="['pi', sidebarOpen ? 'pi-chevron-up' : 'pi-chevron-down']"></i>
        </div>
      </div>

      <!-- ── Sidebar ── -->
      <aside class="ccb-sidebar" :class="{ 'sidebar-open': sidebarOpen }">

        <!-- No branch selected -->
        <div v-if="!openedBranchId" class="ccb-sidebar-placeholder">
          <div class="ccb-sidebar-placeholder-icon">👆</div>
          <p>اختر فصلاً من الأعلى لاستعراض الوحدات</p>
        </div>

        <!-- Units accordion -->
        <div v-else-if="units.length">
          <details
            v-for="unit in units"
            :key="unit.id"
            :open="openedUnitId === unit.id"
          >
            <summary
              :class="{ opened: openedUnitId === unit.id }"
              @click.prevent="handleUnitToggle(unit.id)"
            >
              <span class="ccb-lesson-head">
                <span class="title">{{ unit.title }}</span>
                <span class="description" v-if="unit.description">{{ unit.description }}</span>
              </span>
            </summary>

            <!-- Lessons inside unit -->
            <details
              v-for="lesson in unit.lessons || []"
              :key="lesson.id"
              :open="openedLessonId === lesson.id"
            >
              <summary
                :class="{ opened: openedLessonId === lesson.id }"
                @click.prevent="handleLessonToggle(lesson.id)"
              >
                <span class="ccb-lesson-head">
                  <span class="title">{{ lesson.title }}</span>
                  <span class="description" v-if="lesson.description">{{ lesson.description }}</span>
                </span>
              </summary>

              <!-- Content items -->
              <div class="ccb-content-items">
                <div
                  v-for="c in lesson.contents || []"
                  :key="c.id"
                  @click="showContent(c)"
                  :class="['ccb-content-item', { active: selectedContentId === c.id }]"
                >
                  <span class="ccb-content-item-title">{{ c.title }}</span>
                  <span class="ccb-file-icon">
                    <template v-if="c.type === 'video'"><youtubeIcon /></template>
                    <template v-else-if="c.type === 'audio'"><audio /></template>
                    <template v-else-if="c.type === 'pdf'"><pdf /></template>
                    <template v-else>📁</template>
                  </span>
                </div>
              </div>
            </details>
          </details>
        </div>

        <!-- Loading units -->
        <div v-else-if="openedBranchId" class="ccb-sidebar-placeholder">
          <div class="ccb-sidebar-placeholder-icon loading-icon">⏳</div>
          <p>جاري تحميل الوحدات...</p>
        </div>

      </aside>

      <!-- ── Content viewer ── -->
      <main class="ccb-viewer" ref="viewer">

        <!-- Welcome state -->
        <div v-if="!content" class="ccb-viewer-welcome">
          <div class="ccb-welcome-icon">🎬</div>
          <h2>اختر محتوى لعرضه</h2>
          <p>اختر درساً من القائمة لمشاهدة الفيديو أو الاستماع للصوت أو قراءة الملف</p>
        </div>

        <!-- Content player -->
        <div v-else class="ccb-viewer-player">

          <div class="ccb-viewer-header">
            <div class="ccb-content-type-chip">
              {{ getContentTypeIcon(content.type) }}
              {{ content.type === "video" ? "فيديو" : content.type === "audio" ? "صوت" : content.type === "pdf" ? "ملف PDF" : "ملف" }}
            </div>
            <h2 class="ccb-content-title">{{ content?.title }}</h2>
            <p class="ccb-content-description" v-if="content?.description">{{ content?.description }}</p>
          </div>

          <!-- Video -->
          <div class="ccb-player-wrap" v-if="content.type === 'video'">
            <YoutubeVideo
              v-show="activePlayer === 'youtube'"
              :videoId="videoId"
              :type="content.type"
              :key="'yt-' + videoId"
              ref="youtube"
            />
            <HawamPlayer
              v-show="activePlayer === 'hawam'"
              :videoId="videoId"
              :type="content.type"
              :key="'hw-' + videoId"
              ref="hawam"
            />
            <div class="ccb-player-switch">
              <button @click="activePlayer = 'youtube'" :class="{ active: activePlayer === 'youtube' }">
                ▶ المشغّل الأول
              </button>
              <button @click="activePlayer = 'hawam'" :class="{ active: activePlayer === 'hawam' }">
                ▶ المشغّل الثاني
              </button>
            </div>
          </div>

          <!-- Audio -->
          <AudioPlayer
            v-if="content.type === 'audio'"
            :link="content?.file || ''"
            :key="content?.file"
          />

          <!-- PDF -->
          <div v-if="content.type === 'pdf'" class="ccb-pdf-wrap">
            <PdfLearningViewer
              :pdf-url="content?.file || ''"
              :content-id="content?.id"
              :read-only="false"
              :watermark-enabled="true"
              :watermark-lines="[user?.phone]"
            />
          </div>

        </div>

      </main>

    </div>
  </div>
</template>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.ccb-root { direction: rtl; font-family: 'Cairo', sans-serif; }

/* ─── Branch strip ─── */
.ccb-branches-strip {
  display: flex; align-items: center; gap: .65rem;
  flex-wrap: wrap; margin-bottom: 1.5rem; padding: .25rem 0;
}
.ccb-branch-item {
  padding: .3rem .6rem; border: 2px solid rgba(91,95,239,.14);
  background: #fff; cursor: pointer;
  font-weight: 600; font-size: .88rem; color: #6B7280;
  transition: all .3s; box-shadow: 0 4px 12px rgba(0,0,0,.06);
  border-radius: 16px; display: flex; flex-direction: column;
  align-items: center; justify-content: center; min-width: 170px;
}
.ccb-branch-item p { margin: 0; }
.ccb-branch-item .ccb-branch-description { font-size: .8rem; color: #9CA3AF; display: block; margin-top: .15rem; }
.ccb-branch-item:hover { border-color: #5B5FEF; color: #5B5FEF; transform: translateY(-2px); box-shadow: 0 12px 32px rgba(91,95,239,.25); }
.ccb-branch-item.active { background: linear-gradient(135deg,#5B5FEF,#7C4DFF); border-color: transparent; color: #fff; box-shadow: 0 12px 32px rgba(91,95,239,.3); }
.ccb-branches-empty { color: #9CA3AF; font-size: .88rem; }

/* ─── Layout ─── */
.ccb-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1.25rem;
  align-items: start;
}
.ccb-sidebar-toggle-btn { display: none; }
.ccb-mobile-panel-header { display: none; }

/* ─── Sidebar ─── */
.ccb-sidebar {
  background: #fff; border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0,0,0,.07);
  border: 1px solid rgba(91,95,239,.08);
  overflow: hidden; position: sticky; top: 80px;
  max-height: calc(100vh - 100px); overflow-y: auto;
}
.ccb-sidebar::-webkit-scrollbar { width: 4px; }
.ccb-sidebar::-webkit-scrollbar-track { background: transparent; }
.ccb-sidebar::-webkit-scrollbar-thumb { background: rgba(91,95,239,.2); border-radius: 99px; }

.ccb-sidebar-placeholder {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 3rem 1.5rem; text-align: center;
  color: #9CA3AF; gap: .75rem;
}
.ccb-sidebar-placeholder-icon { font-size: 2.5rem; }
.ccb-sidebar-placeholder p { font-size: .88rem; line-height: 1.6; }

.ccb-sidebar details { border-bottom: 1px solid rgba(91,95,239,.06); }
.ccb-sidebar details:last-child { border-bottom: none; }
.ccb-sidebar summary {
  padding: .9rem 1rem; cursor: pointer;
  font-weight: 600; font-size: .88rem;
  display: flex; justify-content: space-between; align-items: center;
  transition: all .25s; list-style: none;
  color: #1A1A1A; background: #fff;
}
.ccb-sidebar summary::-webkit-details-marker { display: none; }
.ccb-sidebar summary::after {
  content: '▾'; font-size: .9rem; color: #5B5FEF;
  transition: transform .25s; flex-shrink: 0; margin-right: .25rem;
}
.ccb-sidebar details[open] > summary::after { transform: rotate(180deg); }
.ccb-sidebar summary .ccb-lesson-head { display: flex; flex-direction: column; gap: .1rem; flex: 1; }
.ccb-sidebar summary .title { font-size: .88rem; font-weight: 600; }
.ccb-sidebar summary .description { font-size: .73rem; color: #9CA3AF; }
.ccb-sidebar summary.opened,
.ccb-sidebar summary:hover {
  background: linear-gradient(135deg,rgba(91,95,239,.05),rgba(124,77,255,.04));
  color: #5B5FEF;
}
.ccb-sidebar summary.opened .title,
.ccb-sidebar summary:hover .title { color: #5B5FEF; }

.ccb-sidebar details details {
  background: rgba(91,95,239,.02);
  border-bottom: none;
}
.ccb-sidebar details details summary {
  font-size: .83rem; padding: .7rem 1rem .7rem 1.5rem;
  border-bottom: 1px solid rgba(91,95,239,.05);
}

.ccb-content-items { padding: .35rem .5rem; background: rgba(245,247,251,.9); }
.ccb-content-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: .5rem .75rem; margin-bottom: .2rem;
  border-radius: 8px; cursor: pointer;
  transition: all .15s; font-size: .83rem; color: #6B7280;
  border: 1px solid transparent;
}
.ccb-content-item-title { flex: 1; }
.ccb-file-icon svg { width: 18px; height: 18px; }
.ccb-content-item:hover {
  background: rgba(91,95,239,.08); color: #5B5FEF;
  border-color: rgba(91,95,239,.14);
}
.ccb-content-item.active {
  background: linear-gradient(135deg,rgba(91,95,239,.1),rgba(124,77,255,.08));
  color: #5B5FEF; border-color: rgba(91,95,239,.2); font-weight: 600;
}

/* ─── Viewer ─── */
.ccb-viewer {
  background: #fff; border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0,0,0,.07);
  border: 1px solid rgba(91,95,239,.08);
  min-height: 60vh; overflow: hidden;
}

.ccb-viewer-welcome {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 60vh; text-align: center; padding: 3rem; gap: .85rem; color: #6B7280;
}
.ccb-welcome-icon { font-size: 4rem; margin-bottom: .5rem; }
.ccb-viewer-welcome h2 { font-size: 1.35rem; font-weight: 700; color: #1A1A1A; }
.ccb-viewer-welcome p { font-size: .9rem; max-width: 380px; line-height: 1.7; }

.ccb-viewer-player { padding: 1.5rem; }
.ccb-viewer-header { margin-bottom: 1.25rem; }
.ccb-content-type-chip {
  display: inline-flex; align-items: center; gap: .4rem;
  background: rgba(91,95,239,.07); color: #5B5FEF;
  padding: .25rem .85rem; border-radius: 99px;
  font-size: .78rem; font-weight: 700; margin-bottom: .6rem;
}
.ccb-content-title { font-size: 1.35rem; font-weight: 700; color: #1A1A1A; margin-bottom: .35rem; padding-bottom: .5rem; border-bottom: 2px solid rgba(91,95,239,.1); }
.ccb-content-description { color: #6B7280; font-size: .88rem; margin-bottom: .75rem; }

.ccb-player-wrap { margin-bottom: 1rem; }

.ccb-pdf-wrap {
  display: flex; flex-direction: column; gap: 1rem;
  min-height: 75vh;
}

.ccb-player-switch { display: flex; gap: .5rem; margin-top: .75rem; flex-wrap: wrap; }
.ccb-player-switch button {
  background: transparent; border: 2px solid rgba(91,95,239,.25);
  color: #5B5FEF; padding: .45rem 1.1rem; border-radius: 99px;
  font-weight: 600; font-size: .85rem; cursor: pointer;
  transition: all .25s; font-family: 'Cairo', sans-serif;
}
.ccb-player-switch button:hover, .ccb-player-switch button.active {
  background: linear-gradient(135deg,#5B5FEF,#7C4DFF);
  border-color: transparent; color: #fff; box-shadow: 0 8px 20px rgba(91,95,239,.25);
}

/* ─── Responsive: tablet (960px) ─── */
@media(max-width: 960px) {
  .ccb-layout { grid-template-columns: 1fr; }
  .ccb-sidebar-toggle-btn {
    display: flex; align-items: center; gap: .5rem;
    background: rgba(91,95,239,.08); border: 1.5px solid rgba(91,95,239,.18);
    color: #5B5FEF; padding: .55rem 1.25rem; border-radius: 99px;
    font-weight: 700; font-size: .88rem; cursor: pointer;
    transition: all .25s; font-family: 'Cairo', sans-serif;
    width: fit-content; margin-bottom: .5rem;
  }
  .ccb-sidebar-toggle-btn:hover { background: rgba(91,95,239,.14); }
  .ccb-sidebar { position: relative; top: 0; max-height: none; display: none; }
  .ccb-sidebar.sidebar-open { display: block; }
}

/* ─── Responsive: mobile (768px) ─── */
@media(max-width: 768px) {
  .ccb-branches-strip {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: .6rem;
    margin-bottom: 0 !important;
  }
  .ccb-branch-item {
    display: flex; align-items: center; justify-content: center;
    text-align: center;
    min-height: 54px;
    padding: .7rem .6rem !important;
    font-size: .85rem !important;
    font-weight: 700;
    border-radius: 16px;
    border: 1.5px solid rgba(91,95,239,.1);
    box-shadow: 0 2px 10px rgba(17,24,39,.06);
  }
  .ccb-branch-item.active { box-shadow: 0 8px 22px rgba(91,95,239,.28); }

  .ccb-layout {
    grid-template-columns: 1fr !important;
    gap: .65rem !important;
    min-width: 0 !important;
    width: 100% !important;
  }

  .ccb-sidebar-toggle-btn { display: none !important; }

  .ccb-mobile-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: .9rem 1.1rem;
    background: linear-gradient(135deg, rgba(91,95,239,.07), rgba(124,77,255,.05));
    border: 1.5px solid rgba(91,95,239,.18);
    border-radius: 14px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background .18s, transform .15s;
    box-shadow: 0 2px 10px rgba(91,95,239,.1);
    user-select: none;
    margin-bottom: .65rem;
  }
  .ccb-mobile-panel-header:active { transform: scale(0.98); background: rgba(91,95,239,.12); }

  .ccb-panel-header-label { display: flex; align-items: center; gap: .6rem; font-weight: 700; color: #5B5FEF; font-size: .95rem; }
  .ccb-panel-header-label .pi { font-size: 1rem; }
  .ccb-panel-badge {
    background: #5B5FEF; color: #fff;
    border-radius: 99px; font-size: .7rem; font-weight: 700;
    padding: .1rem .5rem; line-height: 1.6;
    min-width: 22px; text-align: center;
  }
  .ccb-panel-header-action { display: flex; align-items: center; gap: .35rem; color: #5B5FEF; font-size: .82rem; font-weight: 600; }
  .ccb-panel-header-action .pi { font-size: .9rem; transition: transform .25s; }

  .ccb-sidebar {
    display: none !important;
    position: relative !important;
    top: auto !important; right: auto !important; bottom: auto !important;
    width: 100% !important; max-width: 100% !important;
    z-index: auto !important; overflow-y: visible !important;
    max-height: none !important; transform: none !important;
    transition: none !important;
    border-radius: 14px !important;
    box-shadow: 0 4px 16px rgba(0,0,0,.08) !important;
  }
  .ccb-sidebar.sidebar-open { display: block !important; }

  .ccb-viewer { border-radius: 14px !important; min-height: 50vh !important; min-width: 0 !important; width: 100% !important; }
  .ccb-viewer-player { padding: .85rem !important; min-width: 0 !important; }
  .ccb-content-title { font-size: 1.1rem !important; }
  .ccb-pdf-wrap { min-height: 60vh !important; min-width: 0 !important; width: 100% !important; }

  .ccb-sidebar summary { padding: 1rem 1rem !important; min-height: 52px; }
  .ccb-sidebar details details summary { padding: .85rem 1rem .85rem 1.35rem !important; }
  .ccb-content-item { padding: .8rem 1rem !important; min-height: 48px; }
  .ccb-content-item-title { font-size: .88rem !important; }
}

@media(min-width: 480px) and (max-width: 768px) {
  .ccb-branches-strip { grid-template-columns: repeat(4, 1fr); }
}

@media(max-width: 600px) {
  .ccb-viewer-player { padding: .75rem !important; }
  .ccb-pdf-wrap { min-height: 52vh !important; }
}
</style>
